import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeRu from '@angular/common/locales/ru';
import localeUk from '@angular/common/locales/uk';
import type { ApplicationConfig } from '@angular/core';
import { ErrorHandler, inject, isDevMode, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router } from '@angular/router';

import { authInterceptor } from '@rpgc-ai/services/auth/auth.interceptor';
import { CDN_REPLACE_CONFIG,CDN_REPLACE_TOKEN } from '@rpgc-ai/services/cdn-replace/cdn-replace.data';
import { MODAL_REGISTRY } from '@rpgc-ai/services/modal/modal-registry.token';
import {
  CAICHAT_SOCIALS_CONFIG,
  CAICHAT_SUPPORT_EMAIL,
  SOCIAL_LINKS_TOKEN,
  SUPPORT_EMAIL_TOKEN,
} from '@rpgc-ai/services/social-link/social-link.const';
import { storageServicePersistStorageFactory } from '@rpgc-ai/services/storage/storage.service';
import { SUPPORTED_LANGUAGES_CODE } from '@rpgc-ai/services/translate/languages';
import { TranslocoHttpLoader } from '@rpgc-ai/services/translate/transloco-loader';

import { MODAL_SHELL_COMPONENT } from '@rpgc-ai/shared-interfaces/tokens/modal.token';

import { IS_RPGC, IS_TMA } from '@rpgc-ai/environments';
import { ModalComponent } from '@rpgc-ai/lazy-blocks/modal/modal.component';

import { provideTransloco } from '@jsverse/transloco';
import type { GetLangParams } from '@jsverse/transloco-persist-lang';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { provideHighcharts } from 'highcharts-angular';

import { appRoutes } from './app.routes';
import { modalRegistry } from './modal.registry';

// Регистрация РУ локали, чтобы в angular-пайпах можно было использовать ru-RU
registerLocaleData(localeDe);
registerLocaleData(localeRu);
registerLocaleData(localeUk);

// Определяем базовых провайдеров, которые будут во всех режимах
const baseProviders = [
  provideZonelessChangeDetection(),
  provideClientHydration(withEventReplay()),
  provideAnimationsAsync(),
  provideRouter(appRoutes),
  provideStoreDevtools({
    maxAge: 25, // Количество сохраняемых состояний
    logOnly: !isDevMode(), // Ограничение для продакшена
    autoPause: true, // Автоматическая пауза при навигации
    trace: false, // Трассировка действий
    traceLimit: 75, // Лимит трассировки
    connectInZone: false, // Отключаем подключение в зоне Angular, т.к. используем zoneless режим
  }),
  provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
  provideTransloco({
    config: {
      availableLangs: SUPPORTED_LANGUAGES_CODE,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
      missingHandler: {
        useFallbackTranslation: false,
      },
    },
    loader: TranslocoHttpLoader,
  }),
  provideTranslocoPersistLang({
    storage: { useFactory: storageServicePersistStorageFactory },
    storageKey: 'selectedLanguage',
    getLangFn: ({ cachedLang, browserLang, defaultLang }: GetLangParams) => {
      return cachedLang || browserLang || defaultLang;
    },
  }),
  provideHighcharts(),
  { provide: IS_RPGC, useValue: false },
  { provide: IS_TMA, useValue: false },
];

// Определяем провайдеры Sentry, которые будут только в production
const sentryProviders = isDevMode()
  ? []
  : [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        // Получаем сервис через inject (просто для инициализации)
        inject(Sentry.TraceService);
        // Функция должна возвращать void, Promise или Observable
        // Возвращаем void (не функцию)
      }),
    ];

const customProviders = [
  { provide: SOCIAL_LINKS_TOKEN, useValue: CAICHAT_SOCIALS_CONFIG },
  { provide: SUPPORT_EMAIL_TOKEN, useValue: CAICHAT_SUPPORT_EMAIL },
  { provide: MODAL_REGISTRY, useValue: modalRegistry },
  { provide: MODAL_SHELL_COMPONENT, useValue: ModalComponent },
  { provide: CDN_REPLACE_TOKEN, useValue: CDN_REPLACE_CONFIG },
];

export const appConfig: ApplicationConfig = {
  providers: [...baseProviders, ...sentryProviders, ...customProviders],
};
